import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Download, ChevronLeft, ChevronRight, Play } from 'lucide-react';

interface MediaFile {
  fileName: string;
  fileUrl: string;
  type: 'image' | 'video';
  raw?: any;
}

interface MediaViewerProps {
  files: any[];
  initialIndex?: number;
  onClose: () => void;
}

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'];
const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'webm'];

const getFileExtension = (name?: string) => (name || '').split('.').pop()?.toLowerCase() || '';

const normalizeFile = (file: any): MediaFile | null => {
  const fileUrl = file?.fileUrl || file?.file_url || file?.url || '';
  if (!fileUrl) {
    return null;
  }

  const rawFileName = file?.fileName || file?.file_name || fileUrl.split('?')[0].split('/').pop() || 'file';
  const extension = getFileExtension(rawFileName);
  const lowerType = (file?.type || '').toLowerCase();

  if (imageExtensions.includes(extension) || lowerType.startsWith('image/')) {
    return { fileName: rawFileName, fileUrl, type: 'image', raw: file };
  }

  if (videoExtensions.includes(extension) || lowerType.startsWith('video/')) {
    return { fileName: rawFileName, fileUrl, type: 'video', raw: file };
  }

  return null;
};

export const MediaViewer: React.FC<MediaViewerProps> = ({ files, initialIndex = 0, onClose }) => {
  const mediaFiles = useMemo(() => (files || []).map(normalizeFile).filter(Boolean) as MediaFile[], [files]);
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (initialIndex >= 0 && initialIndex < mediaFiles.length) {
      return initialIndex;
    }
    return 0;
  });
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < mediaFiles.length) {
      setCurrentIndex(initialIndex);
    } else {
      setCurrentIndex(0);
    }
  }, [initialIndex, mediaFiles.length]);

  // Keyboard navigation effect - must be called before any early returns
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && mediaFiles.length > 0) {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaFiles.length - 1));
        videoRef.current?.pause();
      }
      if (event.key === 'ArrowRight' && mediaFiles.length > 0) {
        setCurrentIndex((prev) => (prev < mediaFiles.length - 1 ? prev + 1 : 0));
        videoRef.current?.pause();
      }
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [mediaFiles.length, onClose]);

  if (!mediaFiles.length) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90">
        <div className="text-center text-white p-6">
          <p className="text-red-400 mb-4">No images or videos available for preview.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#EB1C37] text-white rounded hover:bg-[#c73d2a]"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const currentFile = mediaFiles[currentIndex];

  const handleDownload = async () => {
    try {
      const response = await fetch(currentFile.fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentFile.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading media file:', error);
      const link = document.createElement('a');
      link.href = currentFile.fileUrl;
      link.download = currentFile.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaFiles.length - 1));
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < mediaFiles.length - 1 ? prev + 1 : 0));
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-black/95 flex flex-col">
      <div className="flex items-center justify-between p-4 bg-black/60 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded transition-colors"
            title="Close (Esc)"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="text-white">
            <h2 className="font-semibold">{currentFile.fileName}</h2>
            <p className="text-sm text-gray-400">
              {currentIndex + 1} of {mediaFiles.length}
            </p>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 bg-[#EB1C37] text-white rounded hover:bg-[#c73d2a] transition-colors"
        >
          <Download className="h-4 w-4" />
          Download
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 relative">
        {mediaFiles.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
              title="Previous (←)"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
              title="Next (→)"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        <div className="max-w-full max-h-full flex items-center justify-center">
          {currentFile.type === 'image' ? (
            <img
              src={currentFile.fileUrl}
              alt={currentFile.fileName}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              src={currentFile.fileUrl}
              controls
              className="max-w-full max-h-[80vh]"
            />
          )}
        </div>
      </div>

      {mediaFiles.length > 1 && (
        <div className="p-4 bg-black/50 border-t border-gray-800 overflow-x-auto">
          <div className="flex gap-2 justify-center">
            {mediaFiles.map((file, index) => (
              <button
                key={`${file.fileUrl}-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all ${
                  index === currentIndex
                    ? 'border-[#EB1C37] scale-110'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                {file.type === 'image' ? (
                  <img src={file.fileUrl} alt={file.fileName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <Play className="h-6 w-6 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaViewer;

